/**
 * Skeleton — Loading placeholder components for reducing Cumulative Layout Shift (CLS).
 *
 * Provides shimmer-animated placeholders with fixed dimensions that match
 * the expected size of the content being loaded. This prevents layout shifts
 * that occur when async content suddenly appears and changes the page layout.
 *
 * Components:
 * - Skeleton       — Base building block (customisable width/height/rounded)
 * - SkeletonCard    — KPI/stat card placeholder
 * - SkeletonChart   — Chart container placeholder (fixed height)
 * - SkeletonListItem— Single list item placeholder
 * - SkeletonList    — Multiple list item placeholders
 *
 * @example
 * // While data loads
 * {isLoading ? <SkeletonCard /> : <KpiCard ... />}
 *
 * // Suspense fallback for lazy-loaded chart
 * <Suspense fallback={<SkeletonChart height={280} />}>
 *   <AccuracyChart data={data} loading={isLoading} />
 * </Suspense>
 *
 * @see https://web.dev/articles/cls
 */

import { clsx } from 'clsx';
import type { CSSProperties } from 'react';

// ============================================================
// Base Skeleton
// ============================================================

export interface SkeletonProps {
  className?: string;
  /** Fixed width — any CSS value (e.g. "100%", "200px", "12rem") or number in px */
  width?: string | number;
  /** Fixed height — any CSS value (e.g. "1rem", "64px", "16rem") or number in px */
  height?: string | number;
  /** Border radius */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  /** Accessible label for screen readers */
  'aria-label'?: string;
}

const roundedMap: Record<NonNullable<SkeletonProps['rounded']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

/**
 * Base skeleton block — an animated placeholder with configurable dimensions.
 */
export function Skeleton({
  className,
  width,
  height,
  rounded = 'md',
  'aria-label': ariaLabel = 'Loading',
}: SkeletonProps) {
  const style: CSSProperties = {
    ...(width != null && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(height != null && { height: typeof height === 'number' ? `${height}px` : height }),
  };

  return (
    <div
      className={clsx('animate-pulse bg-gray-200', roundedMap[rounded], className)}
      style={style}
      role="status"
      aria-label={ariaLabel}
    >
      <span className="sr-only">{ariaLabel}…</span>
    </div>
  );
}

// ============================================================
// SkeletonCard — KPI/stat card placeholder
// ============================================================

/**
 * Skeleton placeholder for KPI/stat cards.
 * Matches the layout of KpiCard with icon, trend badge, label, and value.
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5',
        className,
      )}
      aria-label="Loading statistic card"
      role="status"
    >
      <div className="flex items-center justify-between mb-3">
        <Skeleton width={40} height={40} rounded="lg" aria-label="" />
        <Skeleton width={64} height={24} rounded="full" aria-label="" />
      </div>
      <Skeleton width={80} height={12} className="mb-2" aria-label="" />
      <Skeleton width={96} height={28} aria-label="" />
      <span className="sr-only">Loading statistic card…</span>
    </div>
  );
}

// ============================================================
// SkeletonChart — Chart container placeholder (fixed height)
// ============================================================

/**
 * Skeleton placeholder for chart containers with a fixed height to prevent CLS.
 * Use as Suspense fallback for lazy-loaded chart components.
 *
 * @param height - Fixed height in pixels (default: 256)
 */
export function SkeletonChart({
  height = 256,
  className,
  titleWidth = 160,
}: {
  height?: number;
  className?: string;
  /** Width of the title skeleton bar (px) */
  titleWidth?: number;
}) {
  return (
    <div
      className={clsx(
        'bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6',
        className,
      )}
      style={{ minHeight: height + 48 }}
      aria-label="Loading chart"
      role="status"
    >
      <Skeleton width={titleWidth} height={20} className="mb-4" aria-label="" />
      <Skeleton width="100%" height={height} rounded="lg" aria-label="" />
      <span className="sr-only">Loading chart…</span>
    </div>
  );
}

// ============================================================
// SkeletonListItem — Single list item placeholder
// ============================================================

/**
 * Skeleton placeholder for a single list item.
 * Matches the layout of items in CriticalPointsList and similar lists.
 */
export function SkeletonListItem({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 p-3 rounded-lg border border-gray-100',
        className,
      )}
      aria-label="Loading list item"
      role="status"
    >
      <Skeleton width={36} height={36} rounded="lg" aria-label="" />
      <div className="flex-1 space-y-2">
        <Skeleton width="60%" height={14} aria-label="" />
        <Skeleton width="40%" height={12} aria-label="" />
      </div>
      <Skeleton width={48} height={24} rounded="full" aria-label="" />
      <span className="sr-only">Loading list item…</span>
    </div>
  );
}

// ============================================================
// SkeletonList — Multiple list item placeholders
// ============================================================

/**
 * Skeleton placeholder for a list of items.
 *
 * @param items - Number of skeleton items to render (default: 3)
 */
export function SkeletonList({
  items = 3,
  className,
}: {
  items?: number;
  className?: string;
}) {
  return (
    <div className={clsx('space-y-2', className)} role="status" aria-label={`Loading ${items} items`}>
      {Array.from({ length: items }).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
      <span className="sr-only">Loading list…</span>
    </div>
  );
}

// ============================================================
// SkeletonHeatmap — Activity heatmap placeholder
// ============================================================

/**
 * Skeleton placeholder for the activity heatmap.
 * Approximates the grid layout to prevent CLS.
 */
export function SkeletonHeatmap({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6',
        className,
      )}
      aria-label="Loading activity heatmap"
      role="status"
    >
      <Skeleton width={128} height={20} className="mb-4" aria-label="" />
      <div className="flex gap-[3px] overflow-hidden">
        {Array.from({ length: 52 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-[3px]">
            {Array.from({ length: 7 }).map((_, j) => (
              <div
                key={j}
                className="w-[11px] h-[11px] rounded-[2px] bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Skeleton width={32} height={12} aria-label="" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-[11px] h-[11px] rounded-[2px] bg-gray-100 animate-pulse" />
        ))}
        <Skeleton width={32} height={12} aria-label="" />
      </div>
      <span className="sr-only">Loading activity heatmap…</span>
    </div>
  );
}

// ============================================================
// SkeletonGrid — Grid of skeleton cards
// ============================================================

/**
 * Skeleton placeholder for a grid of KPI cards.
 *
 * @param count - Number of skeleton cards (default: 4)
 * @param cols - Grid columns CSS class (default: "grid-cols-2 lg:grid-cols-4")
 */
export function SkeletonGrid({
  count = 4,
  cols = 'grid-cols-2 lg:grid-cols-4',
  className,
}: {
  count?: number;
  cols?: string;
  className?: string;
}) {
  return (
    <div className={clsx('grid gap-3 sm:gap-4', cols, className)} role="status" aria-label="Loading statistics">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
      <span className="sr-only">Loading statistics…</span>
    </div>
  );
}

export default Skeleton;